import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ORDERS_PAGE_SIZE } from 'constants/pagination';
import useDebounceCallback from 'hooks/useDebounceCallback';
import EmptyOrdersSvg from 'images/v5/empty-states/orders.png';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import SortIcon from 'images/v5/icons/filter-lines.svg';
import HelpCircleSvg from 'images/v5/icons/help-circle.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import LoopSvg from 'images/v5/icons/search-md.svg';
import StarsSvg from 'images/v5/icons/stars-01.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Header from 'layout/atoms/Header/Header';
import Input from 'layout/atoms/Input/Input';
import Loader from 'layout/atoms/Loader/Loader';
import OrdersListItem from 'layout/atoms/OrdersListItem/OrdersListItem';
import StatsCard from 'layout/atoms/StatsCard/StatsCard';
import { Cell, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import FiltersBar from 'layout/molecules/FiltersBar/FiltersBar';
import Grid from 'layout/molecules/Grid/Grid';
import OrdersList from 'layout/molecules/OrdersList/OrdersList';
import Pagination from 'layout/molecules/Pagination';
import Sticky from 'layout/molecules/Sticky';
import { useAppDispatch } from 'store/index';
import { clearBreadcrumbs, openHelpAgent, openPageGuide, setBreadcrumbs } from 'store/layoutSlice';
import { addOrder, fetchOrders, fetchOrdersStats, goToNextPage, goToPage, goToPreviousPage, resetFilters, resetOrdersState, resetPagination, selectCurrentPage, selectDelayedOrderIds, selectOrders, selectOrdersFilters, selectOrdersPageInfo, selectOrdersRefetchFlags, selectOrdersStatsStatus, selectOrdersStatus, selectOrdersTotalCount, selectPageNumbers, selectPlannedOrderIds, setFilterOrder, setFilterSearch, setFilterSeason, setFilterStatuses, setFilters, } from 'store/ordersSlice';
import { fetchSeasons, selectSeasons, selectSeasonsStatus } from 'store/parameters/seasonsSlice';
import Dashboard from 'templates/Dashboard/Dashboard';
import { getMenuLabel } from 'utils/getMenuItem';
const ORDERS_FILTERS_STORAGE_KEY = 'orders_filters';
const loadStoredOrdersFilters = () => {
    var _a;
    try {
        const raw = localStorage.getItem(ORDERS_FILTERS_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        return {
            search: '',
            orderBy: (_a = parsed.orderBy) !== null && _a !== void 0 ? _a : '-modifiedDate',
            statuses: Array.isArray(parsed.statuses) ? parsed.statuses : [],
            // If seasonId is absent or falsy, treat as null so workingSeason can be applied
            seasonId: typeof parsed.seasonId === 'string' && parsed.seasonId ? parsed.seasonId : null,
        };
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load stored orders filters', error);
        return null;
    }
};
const persistOrdersFilters = (filters) => {
    try {
        const filtersToStore = Object.assign(Object.assign({}, filters), { name: undefined, seasonId: filters.seasonId || undefined });
        localStorage.setItem(ORDERS_FILTERS_STORAGE_KEY, JSON.stringify(filtersToStore));
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to persist orders filters', error);
    }
};
function OrdersDashboard() {
    const { t, i18n } = useTranslation(['common', 'orders']);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const orders = useSelector(selectOrders);
    const status = useSelector(selectOrdersStatus);
    const statsStatus = useSelector(selectOrdersStatsStatus);
    const pageInfo = useSelector(selectOrdersPageInfo);
    const currentPage = useSelector(selectCurrentPage);
    const pageNumbers = useSelector(selectPageNumbers);
    const totalCount = useSelector(selectOrdersTotalCount);
    // const errors = useSelector(selectOrdersErrors);
    const refetchFlags = useSelector(selectOrdersRefetchFlags);
    const filters = useSelector(selectOrdersFilters);
    const seasons = useSelector(selectSeasons);
    const seasonsStatus = useSelector(selectSeasonsStatus).fetch;
    const delayedOrderIds = useSelector(selectDelayedOrderIds);
    const plannedOrderIds = useSelector(selectPlannedOrderIds);
    const filtersHydratedRef = useRef(false);
    const workingSeasonAppliedRef = useRef(false);
    const fetchOrdersData = useCallback(() => {
        dispatch(fetchOrders());
        dispatch(fetchOrdersStats());
    }, [dispatch]);
    const debouncedSetFilterName = useDebounceCallback((value) => dispatch(setFilterSearch({ value })), 500);
    const handleSeasonSelect = useCallback((value) => {
        // Mark that user made an explicit choice, so fallback won't override it
        workingSeasonAppliedRef.current = true;
        dispatch(setFilterSeason({ value }));
    }, [dispatch]);
    const statusesOptions = [
        {
            text: i18n.t('orders:status.DRAFT'),
            value: 'DRAFT',
        },
        {
            text: i18n.t('orders:status.APPROVED'),
            value: 'APPROVED',
        },
        {
            text: i18n.t('orders:status.CANCELLED'),
            value: 'CANCELLED',
        },
        {
            text: i18n.t('orders:status.DONE'),
            value: 'DONE',
        },
    ];
    const orderOptions = [
        {
            text: i18n.t('common:modified_date'),
            value: 'modifiedDate',
        },
        {
            text: i18n.t('common:created_date'),
            value: 'createdDate',
        },
    ];
    // Hydrate filters from localStorage once on mount
    useEffect(() => {
        if (filtersHydratedRef.current)
            return;
        const storedFilters = loadStoredOrdersFilters();
        if (storedFilters) {
            dispatch(setFilters(Object.assign(Object.assign(Object.assign({}, filters), storedFilters), { seasonId: storedFilters.seasonId || filters.seasonId || null })));
        }
        filtersHydratedRef.current = true;
    }, [dispatch, filters]);
    useEffect(() => {
        if (seasonsStatus === 'idle') {
            dispatch(fetchSeasons());
        }
    }, [dispatch, seasonsStatus]);
    useEffect(() => {
        if (refetchFlags.fetch) {
            fetchOrdersData();
        }
    }, [dispatch, fetchOrdersData, refetchFlags.fetch]);
    useEffect(() => {
        if (seasonsStatus === 'succeeded') {
            const selectedSeasonExists = filters.seasonId && (seasons === null || seasons === void 0 ? void 0 : seasons.some((season) => season.id === filters.seasonId));
            const workingSeason = seasons === null || seasons === void 0 ? void 0 : seasons.find((season) => season.workingSeason);
            // If no season selected by the filter, default to workingSeason first; fetch will run on next effect tick
            if (workingSeason && !selectedSeasonExists && !workingSeasonAppliedRef.current) {
                workingSeasonAppliedRef.current = true;
                dispatch(setFilterSeason({ value: workingSeason.id }));
                return;
            }
            dispatch(resetPagination());
            fetchOrdersData();
        }
    }, [
        dispatch,
        fetchOrdersData,
        filters.search,
        filters.orderBy,
        filters.seasonId,
        filters.statuses,
        seasons,
        seasonsStatus,
    ]);
    useEffect(() => {
        const label = getMenuLabel('/orders', t);
        if (label) {
            dispatch(setBreadcrumbs([{ to: '/orders', name: label }]));
        }
        return () => {
            dispatch(clearBreadcrumbs());
        };
    }, [dispatch, t]);
    useEffect(() => {
        return () => {
            dispatch(resetOrdersState());
            dispatch(resetFilters());
        };
    }, [dispatch]);
    // Persist filters whenever they change (after initial hydration)
    useEffect(() => {
        if (!filtersHydratedRef.current)
            return;
        persistOrdersFilters(filters);
    }, [filters]);
    const handleAddOrder = useCallback(() => {
        const action = dispatch(addOrder());
        action.unwrap().then((response) => {
            navigate(`/orders/creator/${response.id}`);
        });
    }, [dispatch, navigate]);
    const handleNextPage = useCallback(() => {
        dispatch(goToNextPage());
    }, [dispatch]);
    const handlePreviousPage = useCallback(() => {
        dispatch(goToPreviousPage());
    }, [dispatch]);
    const handleGoToPage = useCallback((page) => {
        dispatch(goToPage(page));
    }, [dispatch]);
    const selectedSeason = filters.seasonId && (seasons === null || seasons === void 0 ? void 0 : seasons.length)
        ? seasons.find((season) => season.id === filters.seasonId)
        : null;
    const seasonButtonLabel = (selectedSeason === null || selectedSeason === void 0 ? void 0 : selectedSeason.name) || t('orders:all_seasons');
    const hasActiveFilters = Boolean(filters.search) || filters.statuses.length > 0 || Boolean(filters.seasonId);
    const showFilters = hasActiveFilters || (status.fetch === 'succeeded' && orders.length > 0);
    const hasNoOrders = status.fetch === 'succeeded' && orders.length === 0 && !hasActiveFilters;
    let ordersContent;
    if (status.fetch === 'idle' || status.fetch === 'loading') {
        ordersContent = React.createElement(Loader, null);
    }
    else if (orders.length === 0 && hasActiveFilters) {
        ordersContent = (React.createElement(OrdersList, null,
            React.createElement(Row, null,
                React.createElement(Cell, { span: "full" },
                    React.createElement(EmptyState, { header: t('common:no_results_header'), subheader: t('common:no_results_subheader'), noSubheader: true })))));
    }
    else if (orders.length === 0) {
        ordersContent = (React.createElement(EmptyState, { icon: React.createElement("img", { src: EmptyOrdersSvg, alt: "" }), header: t('emptyStates:ORDERS_HEADER'), subheader: t('emptyStates:ORDERS_SUBHEADER') },
            React.createElement(Button, { leftIcon: React.createElement(PlusSvg, null), onClick: handleAddOrder, loading: status.add === 'loading' }, t('emptyStates:ORDERS_ADD_BUTTON'))));
    }
    else {
        ordersContent = (React.createElement(OrdersList, { pagination: React.createElement(Pagination, { currentPage: currentPage, pageNumbers: pageNumbers, pageSize: ORDERS_PAGE_SIZE, hasNextPage: pageInfo.hasNextPage, isLoading: status.fetch === 'loading', itemsCount: orders.length, totalCount: totalCount, onPreviousPage: handlePreviousPage, onNextPage: handleNextPage, onGoToPage: handleGoToPage }) }, orders.map((order) => (React.createElement(OrdersListItem, { key: order.id, order: order })))));
    }
    return (React.createElement(Dashboard, { headerMain: React.createElement(React.Fragment, null,
            React.createElement(Input, { size: "s", leftIcon: React.createElement(LoopSvg, null), placeholder: t('search'), onChange: (e) => debouncedSetFilterName(e.target.value) }),
            React.createElement(Button, { leftIcon: React.createElement(PlusSvg, null), onClick: handleAddOrder, loading: status.add === 'loading' }, t('orders:add_order'))), headerAside: React.createElement(React.Fragment, null,
            React.createElement(Button, { color: "inverse", shape: "squared", leftIcon: React.createElement(StarsSvg, null), onClick: () => dispatch(openHelpAgent()) }),
            React.createElement(Button, { color: "inverse", shape: "squared", leftIcon: React.createElement(HelpCircleSvg, null), onClick: () => dispatch(openPageGuide('orders')) })) },
        React.createElement(Grid, { gap: 0 },
            !hasNoOrders && (React.createElement(Grid, { vp: 16, hp: 20, columns: 3 },
                React.createElement(StatsCard, { color: "alert", label: "Op\u00F3\u017Anione zam\u00F3wienia", number: statsStatus === 'succeeded' ? delayedOrderIds.length : '-', loading: statsStatus === 'loading' || statsStatus === 'idle' }),
                React.createElement(StatsCard, { color: "info", label: "W trakcie realizacji", number: statsStatus === 'succeeded' ? plannedOrderIds.length : '-', loading: statsStatus === 'loading' || statsStatus === 'idle' }),
                React.createElement(StatsCard, { color: "success", label: "Zrealizowane zam\u00F3wienia", number: "-", loading: statsStatus === 'loading' || statsStatus === 'idle' }))),
            showFilters && (React.createElement(Sticky, { shadow: true },
                React.createElement(FiltersBar, { searchInput: filters.search ? (React.createElement(Header, null, t('common:search_results_for', { query: filters.search }))) : (React.createElement("div", null)) },
                    React.createElement(Dropdown, { trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(SortIcon, null), rightIcon: React.createElement(ChevronDownSvg, null) }, t('orders:status')) }, statusesOptions.map((option) => (React.createElement(Button, { key: option.value, align: "left", theme: filters.statuses.includes(option.value) ? 'fill' : 'ghost', onClick: () => dispatch(setFilterStatuses({ value: option.value })) }, option.text)))),
                    React.createElement(Dropdown, { trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(SortIcon, null), rightIcon: React.createElement(ChevronDownSvg, null) }, t('sorting')) },
                        orderOptions.map((option) => (React.createElement(Button, { key: option.value, align: "left", theme: filters.orderBy.includes(option.value) ? 'fill' : 'ghost', onClick: () => dispatch(setFilterOrder({ value: option.value })) }, option.text))),
                        React.createElement("hr", null),
                        React.createElement(Button, { theme: !filters.orderBy.startsWith('-') ? 'fill' : 'ghost', onClick: () => filters.orderBy.startsWith('-') &&
                                dispatch(setFilterOrder({ value: filters.orderBy.replace('-', '') })) }, t('common:ascending')),
                        React.createElement(Button, { theme: filters.orderBy.startsWith('-') ? 'fill' : 'ghost', onClick: () => !filters.orderBy.startsWith('-') &&
                                dispatch(setFilterOrder({ value: `-${filters.orderBy}` })) }, t('common:descending'))),
                    React.createElement(Dropdown, { trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(SortIcon, null), rightIcon: React.createElement(ChevronDownSvg, null), loading: seasonsStatus === 'loading' }, seasonButtonLabel) },
                        React.createElement(Button, { align: "left", theme: !filters.seasonId ? 'fill' : 'ghost', onClick: () => handleSeasonSelect(null) }, t('orders:all_seasons')),
                        seasons.map((season) => (React.createElement(Button, { key: season.id, align: "left", theme: filters.seasonId === season.id ? 'fill' : 'ghost', onClick: () => handleSeasonSelect(season.id) }, season.name))))))),
            React.createElement(Grid, { vp: 12, hp: 24 }, ordersContent))));
}
export default memo(OrdersDashboard);
